import React from "react";

/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { GameClient } from "../../gameClient";
import { UIButton, UIButtonSmall } from "../../styles/ButtonStyle";
import { Colors } from "../../palettes/colorPalette";
import { Sound } from "../../../../shared/sound";
import { getLeagueTier, openDiscord, openSteam } from "../../../../shared/utils";
import { TrainingLevel } from "../../../../shared/playerAccount";
import { ClientEndGameState } from "../../states/clientEndGameState";
import { SimulationConstants } from "../../../../shared/constants";
import { ExperimentalStamp } from "../ExperimentalStamp";
import { getLadderRank, getTrainingLevel } from "../../userSettings";
import { LeagueMarker } from "../TierMarker";
import { getGameStats, getPerRoundMoneyStats } from "../../gameStats";
import { MoneyHistogram } from "../MoneyHistogram";

declare var gameClient: GameClient;

interface GameEndProps {
    round: number;
    maxRounds: number;
}

const containerStyle = css`
    width: 100%;
    height: 100%;
    margin: auto;
    position: absolute;
    display: flex;
    flex-direction: column;
`;

const titleStyle = css`
	background: ${Colors.UIBackground};
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: max-content;
    max-width: calc(100vw - 2rem);
    max-height: calc(100vh - 2rem);
    box-sizing: border-box;
    overflow: auto;
    padding: 1vw;
    border-radius: 2vh;
    border: inset 0.5vw ${Colors.UITextAndBorders};
`;

const hideStyle = css`
    display: none;
`;

const statsPanelStyle = css`
    margin: 0.75rem 0;
    padding: 0.75rem 1rem;
    border: 1px solid ${Colors.UITextAndBorders};
    border-radius: 1vh;
`;

const statRowStyle = css`
    display: flex;
    justify-content: space-between;
    gap: 2rem;
    line-height: 1.25;
`;

export const GameEndScreen = (props: GameEndProps) => {
    const {
        round,
        maxRounds
    } = props;
    const [hide, setHide] = React.useState(false);

    const toggleHide = () => {
        setHide(!hide);
    }

    const goToLobby = () => {
        const state = gameClient.getCurrentState();
        const advanced = state === "end" ? (gameClient.stateSystem.peekState() as ClientEndGameState).hasAdvanced : false;
        gameClient.goToLobby(advanced);
    }

    const startEndlessMode = () => {
        gameClient.startEndlessMode();
    }

    const hover = () => {
        gameClient.playUISound(Sound.Menu);
    }

    const won = gameClient.hasWon();
    const stats = getGameStats();
    const perRoundStats = getPerRoundMoneyStats();

    return (<div css={containerStyle}>
        <div css={css`display:${hide ? 'none' : 'flex'}; flex-direction: column; align-items:center;`}>
            <img css={css`width:50vh; height: auto;`} src="assets/ui/logo.png" />
            <img css={css`
                    width: 100%;
                    height: auto;
                    position: absolute;
                    left: 0;
                    pointer-events: none;
                    top: 0;   
                `} src="assets/ui/splash.png" />
        </div>
        <div css={hide ? hideStyle : titleStyle}>
            <div css={css`
                text-align: end;
                font-size: 1em;
                font-weight: bold;
            `}>
                <span onClick={toggleHide}>X</span>
            </div>

            {
                <h2>You lasted {round} rounds {round >= maxRounds && "and fought off the viruses!"}</h2>
            }
            {
                // Figure out if you've won the game
                won && gameClient.isRankedGame && getLadderRank() > 0 && getLadderRank() % 3 === 0 &&
                <h3>Congratulations! You have been promoted to <LeagueMarker rank={getLadderRank()} /> rank!</h3>
            }
            {won && getTrainingLevel() < TrainingLevel.Trained && <p>Game difficulty increased, play a new game to try new immune cells!</p>}
            <div css={statsPanelStyle}>
                <h3 css={css`margin: 0 0 0.5rem;`}>Your Stats</h3>
                <div css={statRowStyle}><span>Money spent</span><strong>{stats.moneySpent}</strong></div>
                <div css={statRowStyle}><span>Money earned</span><strong>{stats.moneyEarned}</strong></div>
                <div css={statRowStyle}><span>Experience purchased</span><strong>{stats.experienceBought}</strong></div>
                <div css={statRowStyle}><span>Shop refreshes</span><strong>{stats.refreshesBought}</strong></div>
                <MoneyHistogram data={perRoundStats} />
            </div>
            {
                process.env.is_demo &&
                <>
                    <p>We're a team of two working on the game, and we'd love to have your feedback.</p>
                    <p>The full game will have expanded features including:</p>
                    <ul>
                        <li><b>Multiplayer</b></li>
                        <li><b>Even More</b> immune cells and viruses</li>
                        <li>And more to come!</li>
                    </ul>
                    <p>If you like the game, wishlist it on steam!</p>
                    <p>Join our Discord for the latest updates.</p>
                </>
            }
            <div style={{
                display: "flex",
                flexDirection: "row",
                alignContent: "center",
                justifyContent: "space-evenly",
            }}>
                <UIButtonSmall onClick={goToLobby}>Back</UIButtonSmall>
                {
                    process.env.is_demo &&
                    <UIButtonSmall onMouseEnter={hover} onClick={openSteam}>Wishlist!</UIButtonSmall>
                }
                {
                    gameClient.hasWon() && gameClient.players.length === 1 && gameClient.trainingLevel === TrainingLevel.Trained &&
                    <ExperimentalStamp fontSize="0.5vw">
                        <UIButtonSmall onMouseEnter={hover} onClick={startEndlessMode}>Endless Mode</UIButtonSmall>
                    </ExperimentalStamp>
                }
            </div>
        </div>

        <UIButtonSmall style={{
            position: "absolute",
            bottom: "1vh",
            left: "1vh",
        }} onClick={goToLobby}>Back</UIButtonSmall>
    </div>);
};